// Centralized patch notes data source
export const patchNotesContent = {
  'all': `# Pokématch VS Logger - Patch Notes

## Updates (v2.6.0) - October 21, 2026

### Public API, New Sets & Fixes

#### 🔌 Public API for Your Match Logs
- **Upload logs automatically**: A new API lets your own scripts and tools export, add, edit and remove your TCG and VGC match logs
- **Personal API tokens**: Create tokens under Profile Settings → Account → API Tokens (your current password is required). Each token is shown once, can be revoked at any time, and shows when it was last used
- **Bulk uploads**: Send up to 100 logs in one request; the response says which were added and why any were rejected. Give each log an **externalId** and resent logs are skipped instead of duplicated; older logs can be given one by editing them
- **Pokémon TCG Live logs**: Send a raw battle log to the new PTCGL endpoint and it is filled in like the website's PTCGL import, with result, turn order, mulligans, prizes, and both decks matched to archetypes with sprites
- **PTCGL import fix**: Games you conceded ("You conceded.") are now recorded as losses instead of wins, and how each game ended is read correctly
- **API Docs page**: A new API Docs page (in the profile menu) covers every endpoint with copy-paste examples and a field reference
- **Secure by default**: Changing or resetting your password revokes every API token

#### 🃏 New Sets
- **30th Celebration** is now the latest Standard set, alongside its Unlimited-only Classic Collection
- **Pitch Black** added in July
- **Deck Builder set filter**: The Set dropdown now lists the Mega Evolution series, from Mega Evolution through 30th Celebration

#### 🐛 Fixes
- **PTCGL import**: Choosing an opponent archetype is now optional (defaults to "Other"), and the Import button explains why it is disabled
- **Deck import**: Basic Energy exported with set codes now imports correctly, and imported deck lists are readable in dark mode
- **Deck icons**: Mega and other special forms no longer show blank icons
- **Card lookups**: Cards from sets that share a code (like 30th Celebration and its Classic Collection) now resolve to the right set
- **Mobile layout**: The feedback button no longer covers the bottom navigation, and the Log Match action bar is more compact
- **Security**: Dependency security updates, and match edits can no longer change which account owns a log

---

## Updates (v2.5.0) - June 1, 2026

### Full Visual Redesign, Streamlined Match Logging & a Smarter Deck Archetype System

#### 🎨 App-Wide Visual Refresh
- **A brand-new look**: Every page has been redesigned around a single, cohesive design system — consistent spacing, cards, colors, and typography that look great in both light and dark mode
- **Rebuilt navigation**: A cleaner desktop top bar, a mobile "More" sheet, and a bottom navigation bar with a one-tap "Log Match" button
- **Faster on mobile**: A lighter initial download and no more layout "jumping" from fonts, icons, and sprites while the page loads

#### 📊 Analytics, Reorganized
- **Unified TCG & VGC Analytics**: Match and deck/team analytics are now combined into a single page with tabs, so everything lives in one place
- **Redesigned analytics**: Global Analytics, Deck Comparison, Matchup Matrix, and Tournament Analytics were rebuilt with cleaner cards, charts laid out in responsive grids, and easier-to-read matchup tables
- **Fixed detail popups**: The deck-matchup and Pokémon/move detail modals now open reliably and are laid out clearly

#### 📝 Streamlined Match Logging (TCG)
- **Redesigned Log Match page**: A compact Match Setup sidebar beside the game details, color-coded Win/Loss/Tie buttons, and a cleaner overall flow
- **Combined prize tracking**: "Prize Card Order" and "Prize Card Contents" are now one **Prize Cards** section — click the number to set the order, and pick the card right below it
- **See the whole game at a glance**: Optional fields now pair side-by-side (mulligans, and result + concede) so the full game fits in view
- **Always-reachable actions**: Complete / Save as Draft / Cancel now hover at the bottom of the screen at all times

#### 📥 Redesigned PTCGL Import
- **Guided two-column flow**: Set up your deck and paste your log on the left; the parsed result and opponent confirmation appear on the right
- **Parsed-result tiles**: Result, who went first, mulligans, prizes, and win condition are shown as clear, color-coded tiles before you import

#### 🛠️ Rebuilt Deck Builder
- **Two-column layout**: Deck details and your deck breakdown on the left, card search and results on the right
- **Cleaner throughout**: Tidied-up inputs, side-by-side deck-icon pickers, a compact 4-tile card-count summary, and a sticky save bar

#### 🃏 Smarter Deck Archetypes & Variants
- **Live, accurate archetype list**: Opponent deck archetypes and their variants now come straight from Limitless' current Standard data, so the list reflects the real, up-to-date metagame (Dragapult, Mega Greninja, Raging Bolt, and more) — ranked by how often each deck is actually being played
- **Better deck icons**: Archetype and variant icons are now sharper and correctly cover special forms (Mega, regional, and paradox Pokémon)

#### 🐛 Fixes
- **Dark mode logo**: The navbar logo no longer shrinks when switching to dark mode
- **Dark mode sort menu**: Sorting dropdown text is now readable in dark mode
- **Opponent "Other" deck field**: The custom deck-name box is now styled to match the archetype dropdown

---

## Updates (v2.4.1) - March 17, 2026

### Bug Fixes & Quality of Life

#### 🐛 Match Import & Stats Fixes
- **Fixed deck import quantities**: Card quantities from Limitless tournament imports are now read correctly instead of defaulting to 1
- **Fixed turn order stats for imported matches**: Imported tournament matches no longer count as "went second" — turn order is now marked as unknown so it doesn't skew your first/second turn statistics
- **Fixed Recent Performance mismatch**: The "Recent Performance (Last 10)" stat now shows the same results on both the Dashboard and TCG Deck Analysis pages

#### 🖥️ UI & Usability Fixes
- **Fixed deck selection swapping back**: Selecting a deck while logging a match no longer reverts to a different deck
- **Fixed dark mode text visibility**: Gray text in match groups, log match forms, and other UI elements is now properly visible in dark mode

---

## Updates (v2.4.0) - March 13, 2026

### Match Import Overhaul, CSV Export & UI Fixes

#### 🔄 Limitless Import Improvements
- **Bulk Tournament Import**: New "Import All" button lets you import all available tournaments at once with live progress tracking
- **Fixed match limit bug**: Matches no longer "fall off" the dashboard after reaching 200 — all matches are now returned and visible
- **Fixed zero-match imports**: Importing a tournament with no matches no longer falsely marks it as imported — you can retry the import
- **Fixed tournament delete**: Deleting an imported tournament now properly removes all associated matches
- **Fixed import metadata**: Deck names, import source, and tournament data are now correctly saved with imported matches

#### 📊 CSV Export
- **VGC match export**: Export Data now works for both TCG and VGC match logs
- **Enhanced TCG export**: CSV exports now include deck name, format, tags, and import source columns

#### 🎨 Modal & UI Fixes
- **Fixed feedback modal layout**: The Submit Feedback modal no longer renders broken with misaligned buttons
- **Fixed Add Friend modal styling**: The Add Friend popup now displays correctly with proper styling
- **Fixed chat button on Safari**: The feedback button in the bottom-right corner now works across all browsers
- **Fixed pie chart legend**: The Opponent Deck Distribution chart legend now shows deck names instead of "undefined"
- **Fixed Tournament Analytics**: The "My Tournament Analytics" page no longer shows "set your username" when your Limitless username is already configured

#### 🃏 Opponent Deck Sprites
- **Secondary opponent sprite**: When selecting an opponent deck variant, the variant's sprite is now saved and displayed on match cards

#### ⚙️ Backend Improvements
- **Smarter tournament refresh**: Tournaments that have been deleted from Limitless are no longer retried every 12 hours
- **Better error logging**: Limitless API errors now include the response body for easier debugging

## Updates (v2.3.2) - October 30, 2025

### Tournament Analytics & Meta Analysis System

#### 🏆 Global Tournament Analytics
- **Meta Snapshot Dashboard**: Comprehensive view of competitive tournament landscape
  - Top 20 most-played deck archetypes with usage rates and performance stats
  - Win rate analysis showing which decks perform best in tournament play
  - Average placing statistics for each archetype
  - Top cut rates revealing which decks make it to elimination rounds
  - Filter by format (Standard/Expanded), date range, and result count
- **Visual Performance Indicators**: Color-coded win rates for instant insights
  - Green (>55%): High-performing archetypes
  - Yellow (50-55%): Moderate performance
  - Red (<50%): Below-average performance
- **Interactive Deck Cards**: Click any archetype to dive deep into 60-card analysis

#### 📊 Personal Tournament Analytics
- **Individual Performance Dashboard**: Track your own competitive tournament history
  - Total tournaments played and best placement achieved
  - Overall win rate across all tournament matches
  - Average placement in competitive events
  - Complete win/loss record with visual statistics
- **Deck Usage Analysis**: Understand your archetype preferences
  - Favorite deck identification based on tournament frequency
  - Performance breakdown for each deck you've played
  - Win rates and average placements per archetype
  - Tournament count for each deck in your arsenal
- **Recent Tournament History**: Chronological view of your competitive journey
  - Last 10 tournaments with dates, placements, and records
  - Deck used in each event with performance metrics
  - Win rate per tournament for trend analysis
- **Limitless Integration**: Automatic data sync from Limitless TCG platform
  - Set your Limitless username in profile settings
  - Analytics automatically populated from tournament data
  - Seamless integration with existing tournament import system

#### 🃏 Deck Archetype Deep Dive
- **Complete 60-Card Decklist Analysis**: Revolutionary card-by-card performance tracking
  - Full tournament-winning decklists grouped by Pokémon, Trainers, Energy
  - Average card counts showing optimal ratios (e.g., "2.3x Professor's Research")
  - Inclusion rates revealing how often cards appear in lists (e.g., "92% of lists")
  - Count distribution showing variant popularity (e.g., "3x: 45 lists, 4x: 32 lists")
- **Card Performance Analysis**: Click any card to reveal win rate impact
  - Win rates by card count (0x, 1x, 2x, 3x, 4x) showing optimal quantities
  - Average placing for each count configuration
  - Sample size indicators for statistical reliability
  - Visual highlighting of best-performing card counts
  - Statistical warnings for low sample sizes (<10 matches)
- **Matchup-Specific Card Analysis**: Advanced competitive intelligence
  - Select opponent archetype to see card performance in specific matchups
  - Example: "3x Counter Catcher vs Lost Zone Box: 62% win rate"
  - Win/loss records for each card count against chosen opponent
  - Discover tech card effectiveness in key matchups
  - Make informed deckbuilding decisions based on meta matchups
- **Sample Successful Decklists**: Learn from top-performing tournament builds
  - Top 5 highest-placing decklists for each archetype
  - Player names, placements, and tournament dates
  - Win rates for each successful build
  - Reference successful configurations for deck optimization

#### 🔍 Advanced Deck Browser
- **Searchable Archetype Library**: Find decks quickly with smart search
  - Search by deck name or archetype ID
  - Real-time filtering with instant results
  - Shows match count (e.g., "Showing 12 decks matching 'Charizard'")
- **Comprehensive Filtering**: Narrow results to relevant timeframes
  - Format selection (Standard, Expanded, or All)
  - Date range filtering for specific tournament periods
  - Result limit control (Top 10, 20, or 50 decks)
  - Refresh button for manual data updates
- **Rich Deck Cards**: Each archetype displays key competitive metrics
  - Usage rate percentage showing meta share
  - Win rate with color-coded performance indicators
  - Total tournament appearances
  - Average placement across all events
  - Top cut count for elimination round frequency

#### 🎨 Enhanced User Experience
- **Tournament Navigation**: New dedicated "Tournament" section in navigation
  - Desktop navbar dropdown with Analytics, My Analytics, Decks
  - Mobile sidebar menu with same tournament options
  - Available for both authenticated and guest users
  - Context-aware active state highlighting
- **Responsive Design**: Perfect experience on all devices
  - Mobile-optimized card grids and tables
  - Touch-friendly interactive elements
  - Collapsible card analysis panels
  - Adaptive layouts for phones, tablets, desktops
- **Visual Clarity**: Professional analytics presentation
  - Gradient progress bars for win rates
  - Sample size reliability badges
  - Icon-enhanced stat cards
  - Clean, modern card-based layouts
  - Dark mode support throughout

#### ⚙️ Backend Infrastructure
- **Tournament Cache System**: Automated data collection and storage
  - MongoDB collections optimized for fast analytics queries
  - Progressive import with batch processing (5 tournaments at a time)
  - Cron job running every 12 hours for automatic updates
  - Smart duplicate detection preventing re-imports
  - Admin dashboard for cache management and monitoring
- **Analytics Service Layer**: Powerful aggregation pipeline
  - Meta snapshot generation with usage rate calculations
  - Deck archetype analysis with full card breakdowns
  - Card win rate analysis grouping by card counts
  - Matchup-specific performance extraction
  - User tournament history compilation
- **Performance Optimization**: Lightning-fast query responses
  - Denormalized data for reduced query complexity
  - Strategic database indexes on critical fields
  - Efficient aggregation pipelines for complex calculations
  - Pre-computed statistics for instant loading

#### 📈 Data-Driven Insights
- **Statistical Reliability**: Confidence indicators for all metrics
  - Sample size warnings (red: <10, yellow: 10-30, green: 30+)
  - Comprehensive appearance counts for transparency
  - Clear documentation of data sources
  - Filter controls to adjust data timeframes
- **Competitive Intelligence**: Make informed deckbuilding decisions
  - Identify optimal card counts for your archetype
  - Discover which tech cards improve matchup win rates
  - Learn from successful tournament builds
  - Track meta shifts and archetype popularity trends

## Updates (v2.3.1) - October 10, 2025

### Limitless TCG Integration & Decklist Import

#### 🏆 Limitless Tournament Integration
- **Automatic Match Import**: Import your tournament matches directly from Limitless TCG
  - Search for tournaments by player username
  - View tournament details including date, format, and standings
  - Import all matches from a tournament with one click
  - Automatic match group creation for tournaments
  - Prevents duplicate imports with smart tracking system
- **Complete Player Data**: All tournament information automatically extracted
  - Player placement and record (W-L-T)
  - Deck archetype identification
  - Match pairings with round numbers
  - Opponent information and results

#### 🃏 Full Decklist Support
- **Player Decklist Import**: Complete deck composition stored with matches
  - Pokemon cards with counts and names
  - Trainer cards fully cataloged
  - Energy cards tracked
  - Integrated with existing deck management system
- **Opponent Decklist Tracking**: When available, opponent decklists included in match notes
  - Formatted for easy readability
  - Organized by card type (Pokemon, Trainers, Energy)
  - Accessible directly from match details
  - Perfect for post-tournament analysis

#### 📋 Import History Management
- **Tournament Tracking**: View all previously imported tournaments
  - See import dates and tournament details
  - Track your placement and deck used
  - View number of matches imported
  - Delete tournament records if needed (preserves matches)
- **Seamless Workflow**: Streamlined three-step import process
  1. Search by your Limitless username
  2. Select tournament from results
  3. Review details and confirm import

#### 🎨 Visual & UX Improvements
- **New Favicon**: App logo now used as favicon across all platforms
  - Multiple sizes for optimal display (16x16, 32x32, 192x192, 512x512)
  - PWA-ready with proper manifest configuration
  - Consistent branding across browser tabs and bookmarks
- **Dashboard Integration**: Easy access to Limitless import
  - "Import Limitless" button on dashboard
  - Available in both modern and classic UI modes
  - Matches existing PTCGL import workflow

#### ⚙️ Technical Architecture
- **Backend Services**: Robust Limitless API integration
  - Service layer for API communication
  - Decklist parser for format conversion
  - Tournament tracking model with duplicate prevention
  - Rate-limited endpoints for security
- **Frontend Components**:
  - Dedicated LimitlessImport page with step-by-step wizard
  - Tournament card display with detailed information
  - Import history view with statistics
  - Responsive design for mobile and desktop

## Updates (v2.3.0) - August 10, 2025

### Major Infrastructure Overhaul & Multi-Language Support

#### 🎯 Local Pokemon TCG Data Integration
- **Replaced External API Dependencies**: Completely removed reliance on Pokemon TCG SDK/API
  - Integrated local JSON data files from official Pokemon TCG data repository
  - Eliminated API rate limiting issues and external service dependencies
  - Significantly improved card search and deck import speeds (10x faster)
  - Added offline capability for card data operations
  - Reduced server costs by eliminating external API calls

#### 🌍 Multi-Language PTCGL Match Log Support
- **Seven Language Support**: Parse match logs in multiple languages
  - 🇬🇧 English
  - 🇪🇸 Spanish (Spain)
  - 🇲🇽 Spanish (Latin America)
  - 🇫🇷 French
  - 🇩🇪 German
  - 🇮🇹 Italian
  - 🇵🇹 Portuguese
- **Auto-Detection**: Automatically identifies log language
- **Manual Override**: Option to manually select language if needed
- **Complete Parsing**: All match data extracted regardless of language

#### 📱 Mobile Experience Enhancements
- **Deck Builder Mobile Optimization**: Complete mobile redesign
  - Fixed import button visibility on mobile devices
  - Responsive card grid (3 columns on mobile, 2 on small screens)
  - Touch-friendly controls with 44x44px minimum touch targets
  - Eliminated horizontal scrolling issues
  - Properly styled pagination with scrollable controls
  - Sticky search controls and filters
  - Full-screen dialog mode on mobile devices

#### 🃏 Extended Card Set Support
- **160+ Set Mappings**: Comprehensive set code support
  - Added support for newest sets: WHT (White Flare), DRI (Destined Rivals), JTG (Journey Together)
  - Fixed PR-SV promo card recognition
  - Fixed parsing for cards with PH/RH suffixes
  - Complete mapping for all sets from Base Set through current
  - Support for both PTCGO and PTCGL set codes

#### 🎨 Deck Building Improvements
- **Default Standard Filter**: Card library now defaults to Standard-legal cards
  - Automatically filters to current Standard format
  - Option to switch to Expanded or view all cards
  - Clear format indicators on cards

- **Comprehensive Set Filter**: All available sets in dropdown
  - Organized by series (Scarlet & Violet, Sword & Shield, etc.)
  - 100+ sets available for filtering
  - Easy navigation through Pokemon TCG history

#### 🔧 Bug Fixes & Performance
- **Import Reliability**: Fixed false "Failed to import" messages
- **Card Display**: Cards now show immediately without requiring search
- **Exact Search**: Improved search to show exact matches (e.g., "Charizard ex" returns only Charizard ex cards)
- **Memory Optimization**: Improved caching and memory management
- **Bundle Size**: Reduced by 15% through code cleanup

#### ⚡ User Experience Improvements
- **PTCGL Import Deck Creation**: New ability to create decks directly from match import
  - "Create New Deck" button on PTCGL import page
  - Opens full deck builder in dialog mode
  - Auto-selects newly created deck for match logging
  - Seamless workflow from deck creation to match import

- **Match Type Modal Enhancement**: Fixed styling issues with match logging options
  - Resolved "Import VGC Replay" button being cut off
  - Improved responsive design for mobile and desktop
  - Better button spacing and accessibility
  - Proper modal sizing to accommodate all three options

- **Deck Builder Layout Fix**: Resolved desktop layout issues
  - Fixed search and filter buttons overlapping on desktop
  - Proper horizontal layout for search controls
  - Maintained mobile optimization while fixing desktop experience
  - Enhanced button sizing and positioning consistency

#### 🧹 Project Cleanup
- **Removed Redundant Code**: Deleted 23+ unused files
  - Entire TypeScript state management system (never integrated)
  - Old documentation and planning files
  - Unused test files and utilities
  - Legacy migration scripts
- **Dependency Optimization**: Removed unused npm packages
- **Code Organization**: Better file structure and naming conventions

## Updates (v2.2.1) - June 4, 2025

### Major Dashboard Overhaul & Analytics Fixes

#### Dashboard Redesign
- **Modern Dashboard UI**: Complete redesign of the dashboard with enhanced performance analysis
  - New modern layout with improved visual hierarchy and card-based design
  - Enhanced performance statistics with trend indicators and detailed metrics
  - Added Pokemon sprite display for team lead pairs instead of text
  - Improved responsive design for mobile and desktop
  - Dark mode support with theme-aware styling

- **Team Management Enhancements**: New team name editing functionality
  - Added ability to edit team names directly from the teams page
  - Modal-based editing interface with validation
  - Support for both API and localStorage persistence
  - Improved team display with tooltips showing full team composition

#### Analytics & Import Fixes
- **TCG Match Analytics**: Fixed multiple critical issues
  - Removed 'All Decks' option from dropdown - now shows one deck at a time for clarity
  - Fixed performance cards not showing data by properly setting default deck selection
  - Fixed pie chart legends to show full deck names instead of truncated versions
  - Corrected match result calculations to properly read from games array
  - Fixed turn order statistics to use correct data fields

- **VGC Analytics Improvements**: Major overhaul of VGC analytics functionality
  - Fixed "uninitialized variable" errors that prevented analytics from loading
  - Changed "Archetypes Faced" to "Unique Opponents" (more appropriate for VGC)
  - Removed 'deleted' team logic - imported teams now treated as normal
  - Added helpful tooltips explaining what each metric represents
  - Set default filter to "All Teams" for better initial user experience
  - Fixed move usage rate calculations and data extraction

- **PTCGL Import Enhancements**: Improved match import functionality
  - Fixed navigation routing from '/import-ptcgl-match' to '/import-match'
  - Enhanced import workflow and error handling
  - Better integration with existing match logging system

#### UI/UX Improvements
- **Navigation Overhaul**: Separated TCG and VGC pages for cleaner navigation
  - Removed shared Lists.js component that used tabs
  - Dedicated pages for TCG Decks (/lists) and VGC Teams (/lists-vgc)
  - Fixed navbar dropdown hover gap issues
  - Each page now has dedicated UI without confusing tab switching

- **Match Display Enhancements**: Improved match history and detail views
  - Fixed variant dropdown to always show archetype variants with "Other" option
  - Enhanced match item styling for better readability
  - Improved mobile responsiveness for match cards
  - Better deck/team selection in edit mode

#### Authentication & Season Management Fixes
- **Auth Endpoint Errors**: Fixed 404 errors appearing on every page load/refresh
  - Corrected prefetch API calls to use proper '/api/auth/user' endpoint instead of '/auth/me'
  - Eliminated console errors that were occurring during page navigation
  - Improved authentication state management for smoother user experience

- **Season Display**: Fixed analysis panel showing incorrect season information
  - Dashboard now correctly displays current season name instead of extracting year from start date
  - Example: "2025 Season Record" instead of "2024 Season Record" when season starts in December 2024
  - Analysis panel automatically updates when season changes

- **Owner Role Access**: Enhanced seasons management page permissions
  - Users with 'owner' role can now access and manage seasons alongside admins
  - Unified permission checks to allow both 'admin' and 'owner' roles
  - Maintains existing security while expanding access for appropriate users

#### Technical Improvements
- **Performance Optimizations**: Enhanced rendering and data handling
  - Improved component loading and state management
  - Better error handling throughout the application
  - Enhanced API configuration and endpoint consistency
  - Fixed infinite loops in match logging that caused API spam

- **Code Quality**: Major refactoring and cleanup
  - Removed redundant components and consolidated functionality
  - Enhanced archetype and variant handling
  - Improved deck categorization and opponent deck selection
  - Better separation of concerns between components

- **Deployment Fixes**: Multiple improvements for production stability
  - Fixed Render deployment configuration
  - Enhanced Redis and caching setup
  - Improved environment variable handling
  - Better error logging and monitoring

## Updates (v2.2.0)

### VGC Match Enhancements & Non-Authenticated User Support

#### New Features
- **VGC Brought Pokémon Tracking**: Enhanced VGC match logging to track which Pokémon were brought to battle
  - Automatically extracts brought Pokémon data from replay logs for accurate team analysis
  - Stores both player and opponent brought Pokémon at match and game levels
  - Analytics now show usage statistics only for Pokémon actually brought to battle
  - Ensures accurate representation of team usage in competitive play

- **Non-Authenticated User Support**: Complete support for users without accounts
  - Local active deck management for TCG match logging
  - Deck selection persists across sessions using browser local storage
  - Fixed "no active deck" error when logging matches without being logged in
  - Seamless experience for casual users who don't want to create an account

#### Bug Fixes
- **VGC Sprite Display**: Fixed critical sprite-to-name mapping issue in match display
  - Resolved mismatched sprites showing wrong Pokémon (e.g., Calyrex showing Whimsicott sprite)
  - Created shared PokemonSprite component for consistent async sprite loading
  - Fixed player team sprites not appearing in match detail popups
  - Improved sprite caching and loading performance

- **Authentication State**: Fixed logout not properly clearing authenticated state
  - Added browser refresh on logout to ensure clean state reset
  - Prevents cached authentication data from persisting after logout
  - Resolves issues with deck selection after logging out and back in

- **Deck Access Control**: Fixed 401 errors for non-authenticated users
  - Non-authenticated users can now properly manage and select active decks
  - Local storage fallback for deck management when not logged in
  - Improved error handling for API calls based on authentication status

#### Technical Improvements
- **Database Schema**: Enhanced VGC match schema with brought Pokémon fields
  - Added broughtPokemon and opponentBroughtPokemon arrays to match model
  - Backward compatible with existing matches (graceful fallback to full teams)
  - Improved data accuracy for competitive analytics

- **Component Architecture**: 
  - New reusable PokemonSprite component with error handling and loading states
  - Enhanced local storage utilities for non-authenticated user data
  - Improved separation of concerns between authenticated and local data management

- **Analytics Accuracy**: VGC usage statistics now properly reflect competitive reality
  - Only counts Pokémon that were actually brought to matches
  - Removes misleading data from bench Pokémon that never saw play
  - More accurate representation of the actual metagame
  - **Type-Safe Operations**: Robust error handling for all storage operations
  - **Data Validation**: Ensures integrity of locally stored information
  - **Fallback Mechanisms**: Graceful degradation when API unavailable
  - **Memory Management**: Efficient storage and retrieval of large datasets

- **Enhanced State Management**: Context providers handle dual data sources
  - **Dual-Source Contexts**: All contexts (Deck, Team, Match) support local and API data
  - **Automatic Fallbacks**: 401 errors trigger local storage usage
  - **Transparent Operations**: Components work identically with local or server data
  - **Optimized Performance**: Minimal overhead for local storage operations

- **Robust Sync Infrastructure**: Reliable data transfer system
  - **Batch Processing**: All data types synced in single operation
  - **Error Isolation**: Individual sync failures don't affect others
  - **Retry Logic**: Built-in mechanisms for handling temporary failures
  - **Status Tracking**: Comprehensive logging and user feedback

## Updates (v2.1.1)

### Navigation System Overhaul & UI Improvements

#### New Features
- **Unified Dropdown Navigation**: Complete redesign of navigation system with dropdown menus
  - **Analytics Dropdown**: Unified Analytics button with dropdown options for TCG Analytics and VGC Analytics
  - **My Lists Dropdown**: Unified My Lists button with dropdown options for TCG Decks and VGC Teams
  - Available on both desktop navbar and mobile sidebar for consistent experience
  - Smart context-aware navigation - main buttons navigate to appropriate section based on current page
  - Dropdown arrows provide explicit choice between TCG and VGC variants

- **Pokémon Move Usage Analytics**: Revolutionary new feature showing individual Pokémon move usage patterns
  - **Move Usage Pie Charts**: Small, elegant pie charts in each Pokémon usage card displaying top moves
  - **Smart Data Presentation**: Shows top 4 most-used moves plus "Others" category for clarity
  - **Interactive Tooltips**: Hover for detailed move usage statistics and percentages
  - **Comprehensive Backend**: New API endpoint tracking move usage per Pokémon from replay data
  - **Responsive Design**: Charts scale perfectly on mobile and desktop devices
  - **Theme-Aware**: Charts adapt colors to match light/dark theme settings

- **Smart Analytics Navigation**: Analytics link now intelligently navigates based on current context
  - When viewing VGC pages (teams, VGC matches, VGC logs), Analytics navigates to VGC Analytics
  - When viewing TCG pages (decks, TCG matches, TCG logs), Analytics navigates to TCG Analytics
  - Dashboard tab state is respected (VGC tab active → VGC Analytics)
  - Seamless context-aware navigation improves user workflow

#### UI Enhancements
- **Enhanced Navigation UX**: Improved navigation interaction patterns
  - Click-based dropdown functionality (not just hover)
  - Visual feedback with rotating chevron arrows
  - Active state highlighting for current pages
  - Mutual dropdown exclusivity (opening one closes others)
  - Click outside to close functionality

- **Improved Page Spacing**: Added more comfortable spacing between navbar and page content
  - Increased top padding from 80px to 100px for better visual breathing room
  - Enhanced overall page layout and readability

- **Cross-Platform Consistency**: Navigation features work identically on desktop and mobile
  - Desktop navbar dropdown functionality matches mobile sidebar
  - Consistent styling and behavior across all devices
  - Responsive design maintains usability on all screen sizes

#### Technical Improvements
- **Advanced Analytics Backend**: New VGC match controller endpoint for Pokémon-specific move usage statistics
- **Chart.js Integration**: Enhanced chart components with theme-responsive pie charts
- **Data Processing Pipeline**: Sophisticated move data extraction and normalization from replay logs
- **Component Architecture**: New PokemonMovePieChart component with responsive design patterns
- **Dropdown Navigation**: Comprehensive dropdown system in both Navbar.js and Sidebar.js
- **Enhanced State Management**: Improved state controls for dropdown visibility and move usage data
- **Context-Aware Navigation**: Smart routing using useLocation and localStorage
- **CSS Architecture**: Enhanced styling with move chart specific classes and responsive breakpoints

## Updates (v2.1.0)

### Critical UI & Analytics Fixes

#### Bug Fixes
- **Deck View Layout**: Fixed ultra-compact deck detail page to eliminate excessive vertical spacing
  - Resolved CSS conflicts between LayoutFixes.css and decks.css that were causing large gaps
  - Reduced card heights from 70px to 60px for better space utilization
  - Minimized grid gaps to 0.02rem horizontal and 0.01rem vertical
  - Added aggressive CSS overrides with !important flags to prevent future conflicts
  - Cards now display with maximum density for better viewing experience

- **Analytics Pie Chart**: Fixed legend text colors not switching properly with theme changes
  - Enhanced theme detection to check multiple indicators (CSS variables, classes, attributes)
  - Improved MutationObserver to watch both document and body for theme changes
  - Reduced update delays from 50ms to 10ms for instant color switching
  - Added comprehensive fallback detection for dark/light mode
  - Legend text now correctly displays black in light mode and white in dark mode

#### Performance Improvements
- **Theme Switching**: Optimized chart re-rendering with more efficient refresh mechanisms
- **Layout Stability**: Prevented CSS rule conflicts with specific selectors and importance declarations
- **Visual Density**: Maximized screen real estate usage in deck detail views

#### Technical Enhancements
- Added specific CSS overrides to prevent layout fixes from affecting mini-card components
- Implemented robust theme detection across different frameworks and theme systems
- Enhanced chart refresh system to ensure proper color updates on theme changes

## Major Updates (v2.0.9)

### Shared Match Groups & Collaboration
- **Shared Match Groups**: Revolutionary new feature allowing users to invite friends to collaborate on match collections
  - Group owners can invite friends with customizable permissions (View or Edit)
  - Friends can contribute matches to shared groups for team tournaments and collaborative tracking
  - Visual indicators show shared groups and user permissions
  - Complete invitation system with accept/decline functionality
- **Friend Collaboration**: Enhanced social features for competitive players
  - Invite friends to tournament groups and track results together
  - Share match collections with team members or practice partners
  - View shared groups from friends in your match group list

### Analytics & UI Enhancements
- **Enhanced Analytics Page**: Completely redesigned analytics with improved filtering and layout
  - Added match group filtering with smart mutual exclusion from deck filters
  - Implemented gradient progress bars (red → yellow → green) based on win rates
  - Redesigned deck matchup section as sortable list view (highest to lowest win rate)
  - Added clickable matchup details with comprehensive modal view
  - Moved going first/second statistics to individual matchup cards
  - Improved responsive design and mobile optimization
- **Improved Dashboard Layout**: 
  - Reduced main content width by 15% for better readability and focus
  - Enhanced visual hierarchy and content organization
  - Consistent styling improvements across all pages
- **Advanced Filtering**: 
  - Match group filter overrides deck filter for focused analysis
  - Automatic filter switching prevents conflicting selections
  - Visual feedback shows when filters are disabled/enabled

### Technical Improvements
- **Backend Architecture**: Complete shared groups infrastructure
  - Enhanced MatchGroup model with sharing capabilities and invitation system
  - New API endpoints for friend invitations and permission management
  - Robust permission-based access control (view/edit permissions)
  - Secure invitation workflow with status tracking
- **Frontend Components**: New modular components for sharing functionality
  - ShareGroupModal for inviting friends with permission selection
  - InvitationsModal for managing incoming group invitations
  - Enhanced MatchGroups interface with sharing buttons and indicators
  - Updated context providers with new sharing functions
- **Mobile Optimization**: Improved responsive design across all new features
  - Touch-friendly invitation management
  - Optimized modal layouts for small screens
  - Enhanced accessibility for mobile users

### Bug Fixes & Polish
- **Fixed First Prize Advantage Styling**: Consistent styling with other analytics sections
- **Improved Modal Z-Index**: Fixed modal layering issues with navbar/header
- **Enhanced Content Visibility**: Added proper padding to prevent navbar overlap
- **Updated Navigation**: Added patch notes navigation and improved sidebar structure

## Updates (v2.0.9)

### Admin, Filtering & Access Control Updates

#### New Features
- **Advanced Role System**: Added a new role hierarchy for better access control
  - Added "Owner" role above admin with exclusive permissions
  - Implemented secure role verification middleware
  - Enhanced user management capabilities
- **Admin Management**: New dashboard for owner role to manage admins
  - Search for users by username
  - Promote regular users to admin role
  - Demote admins to regular users
  - View all current admins and their status
- **Analytics Filtering**: Enhanced analytics with deck filtering
  - Filter match data by specific deck played
  - More focused analysis of deck performance
  - Improved data visualization by played deck

#### Technical Improvements
- **Role-Based Security**: Implemented robust role-based access control system
  - Added secure middleware for owner-only routes
  - Updated user model with role field
  - Enhanced authentication endpoints with role information
- **Database Connection Handling**: Improved error handling for database operations
  - Added better error messaging for connection issues
  - Enhanced setup scripts with proper error handling
  - Improved debugging information for connection problems

## Updates (v2.0.8)

### Deck Management & Analytics Enhancements

#### New Features
- **Deck Detail Modal**: Added a new comprehensive deck viewer modal
  - Shows complete card lists with counts by category
  - Displays deck statistics and composition breakdown
  - Enhanced visual styling with card type highlighting
- **Deck Management Improvements**: 
  - Added deck copying functionality 
  - Improved deck import with better error handling and naming
  - Enhanced deck builder with reset on close for cleaner sessions
- **Analytics Visualization**: 
  - Added clickable deck names in analytics displays
  - Replaced small sprite images with readable deck names
  - Improved visual indication of deck usage in match history

#### UI Improvements
- **Deck Item Design**: Updated deck card styling with modern design
  - New button layout with improved visual hierarchy
  - Better visibility for active deck indication
  - Consistent styling across light and dark modes
- **Team Item Styling**: Refined active badge presentation for consistency
- **General Visual Enhancements**:
  - Improved spacing and alignment throughout the application
  - Enhanced buttons with new hover and active states
  - Better contrast for text elements in both themes

#### Technical Improvements
- **Export Utilities**: 
  - Improved deck import with name detection
  - Enhanced card type detection logic
  - Better error handling with detailed failure reporting
- **Component Architecture**:
  - New modular approach for deck detail viewing
  - Improved state management for deck operations
  - Optimized rendering for large deck collections

## Updates (v2.0.7)

### PTCGL Match Log Integration & UI Improvements

#### New Features
- **PTCGL Match Log Parser**: Added the ability to automatically import PTCGL match logs
  - Extracts opponent name, match result, mulligans, and prize card information
  - Supports case-insensitive username matching
  - Automatically identifies win/loss status based on log content
- **Bo3/Bo5 Support for PTCGL Imports**: 
  - Added support for multi-game series when importing PTCGL matches
  - Series automatically tracks wins/losses to determine completion
  - Consistently preserves match group and opponent deck selection across games
- **UI Enhancements**:
  - Added toggle option to hide optional questions for faster TCG match logging
  - Updated home page to include latest patch notes section

#### Technical Improvements
- **Performance Optimization**:
  - Fixed issue with excessive API calls in the PTCGL import component
  - Improved state management for multi-game series
  - Enhanced handling of large match logs with better error reporting
- **Bug Fixes**:
  - Fixed match series tracking to correctly detect when a Bo3/Bo5 series is complete
  - Resolved issue with conditional UI updates in match form
  - Added defensive coding to prevent memory leaks from multiple API calls

## Updates (v2.0.6)

### Bug Fixes
- **Fixed Feedback Deletion**: Resolved 500 error when deleting feedback submissions in the admin portal
  - Added proper validation for MongoDB ObjectId format
  - Improved error handling with detailed logging
  - Replaced findByIdAndRemove with deleteOne for better compatibility

### UI Improvements
- **Enhanced Patch Notes Display**: Fixed rendering issues in the announcements page
  - Updated to display patch notes directly in the component instead of fetching from markdown files
  - Eliminated code block rendering issues with proper formatting
  - Improved version selection with dedicated sections for each release

### Technical Improvements
- **Admin Portal Stability**: Enhanced error handling in feedback management
- **Documentation**: Updated deployment and configuration instructions

## Major Updates (v2.0)

### Features
- **VGC Integration**: Complete integration of Pokémon VGC (Video Game Championships) match logging
- **Friends System**: New social features allowing players to connect with each other
- **Notifications**: Real-time updates for friend requests and activities
- **Unified Match System**: Completely redesigned match logging system supporting both TCG and VGC
- **Enhanced Analytics**: Separate analytics views for TCG and VGC with improved metrics
- **Profile Settings**: Expanded user profile management
- **Improved UI**: Redesigned layout with sidebar navigation and notification bell
- **Showdown Team Builder**: Enhanced support for importing Pokémon Showdown team data

### Technical Changes
- **Architecture**: Migrated from single match model to specialized TCGMatch and VGCMatch models
- **API**: Extended API endpoints for VGC matches, friends, and notifications
- **Migration**: Added migration scripts for transitioning data to new schema
- **Deployment**: Updated configuration for Vercel (frontend) and Render (backend) deployment
- **Documentation**: Enhanced README with detailed deployment instructions

## Recent Fixes
- Added Pokémon sprite selection for TCG decks with dynamic searching
- Added retroactive sprite assignment for existing TCG matches
- Fixed 500 error when adding or updating decks
- Fixed logout button not appearing in mobile sidebar menu
- Fixed VGC matches not displaying in Match History
- Fixed profile picture removal functionality in user settings
- Fixed syntax error in LogMatchVGC.js
- Fixed issues with replay handling
- Temporarily removed Best-of-3 replay auto-import feature due to stability issues
- Fixed server-side validation for energy card limits in deck builder
- Removed 4-card limit for energy cards in deck builder
- Fixed authentication issues for production deployment
- Enhanced CORS handling for authentication routes
- Improved error handling throughout the application

## Deployment Updates
- Added configurations for automated deployment on Vercel and Render
- Updated environment variable handling for production environments
- Fixed API URL configuration for production deployment

## Known Issues
- Best-of-3 (BO3) replay auto-import feature has been temporarily disabled

## Coming Soon
- Restore and improve BO3 replay auto-import feature
- Enhanced mobile responsiveness
- Team sharing features
- Tournament tracking and management`,

  '2.2.1': `# PATCH NOTES - VSLogger v2.2.1

## Major Dashboard Overhaul & Analytics Fixes - June 4, 2025

### Revolutionary Dashboard Redesign
- **Complete Modern UI Transformation**: Dashboard rebuilt from ground up with performance-focused design
  - Card-based layout with enhanced visual hierarchy and improved readability
  - Real-time performance metrics with trend indicators and detailed statistics
  - Pokemon sprite integration for team lead pairs replacing text-based display
  - Comprehensive responsive design ensuring perfect experience across all devices
  - Full dark mode support with theme-aware styling throughout

- **Advanced Team Management**: Professional-grade team organization tools
  - Direct team name editing from teams page with validation and error handling
  - Modal-based editing interface with real-time preview and confirmation
  - Dual persistence supporting both API synchronization and localStorage fallback
  - Enhanced team tooltips displaying complete team composition with sprites

### Analytics Revolution
- **TCG Analytics Complete Overhaul**: Resolved critical performance and data display issues
  - Streamlined deck selection removing confusing 'All Decks' option for focused analysis
  - Fixed performance cards displaying accurate data with proper default selections
  - Enhanced pie chart legends showing complete deck names instead of truncated versions
  - Corrected match result calculations ensuring accurate win/loss statistics
  - Fixed turn order analytics using correct data fields for reliable insights

- **VGC Analytics Transformation**: Major functionality improvements across all metrics
  - Eliminated "uninitialized variable" errors that prevented analytics loading
  - Renamed "Archetypes Faced" to "Unique Opponents" for VGC-appropriate terminology
  - Removed problematic 'deleted' team logic treating all teams equally
  - Added comprehensive tooltips explaining metric calculations and significance
  - Set intelligent "All Teams" default filter for optimal initial user experience
  - Fixed move usage calculations with accurate percentage and frequency data

- **Enhanced Import Workflow**: Streamlined PTCGL match import process
  - Fixed navigation routing ensuring proper import page access
  - Enhanced error handling with detailed feedback and recovery options
  - Improved integration with existing match logging infrastructure

### User Experience Excellence
- **Navigation Architecture Redesign**: Eliminated confusing shared components
  - Separated TCG and VGC into dedicated pages removing tab-based confusion
  - Fixed navbar dropdown hover gaps preventing accidental menu closure
  - Enhanced page-specific UI elements for focused user workflows
  - Improved mobile navigation with touch-optimized interface elements

- **Match Management Enhancement**: Comprehensive improvements to match display and editing
  - Fixed variant dropdown ensuring consistent archetype selection with "Other" fallback
  - Enhanced match card styling with improved readability and visual appeal
  - Optimized mobile responsiveness ensuring perfect experience on all screen sizes
  - Improved deck/team selection in edit mode with better state management

### Authentication & System Reliability
- **Authentication System Fixes**: Eliminated persistent console errors and improved reliability
  - Corrected API prefetch calls using proper '/api/auth/user' endpoint
  - Eliminated 404 errors appearing on every page load and navigation
  - Enhanced authentication state management for seamless user experience
  - Improved token handling and validation throughout the application

- **Season Management Enhancement**: Fixed critical season display issues
  - Dashboard now displays accurate current season names instead of date-derived years
  - Fixed season analysis panel showing correct information regardless of start date
  - Automatic season updates when changes occur in administration panel
  - Enhanced owner role access allowing season management alongside admins

### Technical Excellence
- **Performance Revolution**: Comprehensive optimizations across entire application
  - Enhanced component loading with lazy loading and efficient state management
  - Improved error handling preventing crashes and providing user feedback
  - Fixed API configuration ensuring consistent endpoint behavior
  - Eliminated infinite loops in match logging preventing API spam and performance issues

- **Code Quality Initiative**: Major refactoring improving maintainability and reliability
  - Consolidated redundant components reducing bundle size and complexity
  - Enhanced archetype and variant handling with better validation
  - Improved deck categorization with accurate opponent selection
  - Better separation of concerns improving component reusability

- **Production Stability**: Comprehensive deployment and infrastructure improvements
  - Fixed Render deployment configuration ensuring reliable production builds
  - Enhanced caching strategies with Redis optimization where available
  - Improved environment variable handling for secure configuration management
  - Enhanced error logging and monitoring for better issue diagnosis

## Impact
This massive update transforms VSLogger into a professional-grade platform with enterprise-level reliability, intuitive user experience, and comprehensive analytics capabilities. The dashboard redesign alone represents hundreds of hours of optimization work, while the analytics fixes ensure users receive accurate, actionable insights from their match data.`,

  '2.2.0': `# PATCH NOTES - VSLogger v2.2.0

## Guest User Experience & Local Storage Revolution

### Game-Changing Features
- **Complete Guest Access**: Revolutionary approach to platform accessibility
  - **Zero Barrier Entry**: Experience all VSLogger features without account creation
  - **Full Functionality**: Log matches, create decks/teams, view analytics as guest user
  - **Smart Notifications**: Desktop banners and mobile modals inform about local storage
  - **Seamless Experience**: Identical interface for guests and registered users

- **Advanced Local Storage System**: Enterprise-grade client-side data management
  - **Persistent Storage**: All data survives browser restarts and sessions
  - **Comprehensive CRUD**: Full create, read, update, delete operations locally
  - **Unique Identification**: Each local item gets secure, collision-resistant IDs
  - **Type Safety**: Robust error handling and data validation throughout
  - **Performance Optimized**: Minimal impact on application speed and responsiveness

- **Intelligent Data Sync**: Seamless transition from guest to registered user
  - **Automatic Upload**: All local data syncs to database upon login/registration
  - **Batch Processing**: Matches, decks, and teams uploaded together efficiently
  - **Error Recovery**: Failed syncs preserve local data for retry attempts
  - **Progress Tracking**: Real-time notifications keep users informed throughout process
  - **Clean Migration**: Local storage cleared only after successful database sync

### Enhanced User Experience
- **Universal Platform Access**: Same powerful features for everyone
  - **Analytics Dashboard**: View performance metrics with local match data
  - **Team Building**: Create and manage Pokémon teams without registration
  - **Deck Management**: Build TCG deck lists with full search and organization
  - **Match Logging**: Complete match tracking with all advanced features

- **Context-Aware Interface**: Smart adaptations based on user status
  - **Responsive Warnings**: Appropriate notifications for data storage location
  - **Graceful Degradation**: Missing features clearly communicated to guests
  - **Conversion Incentives**: Natural progression points toward account creation
  - **Preserved Context**: User's current work maintained through registration

### Technical Excellence
- **Dual-Source Architecture**: Contexts seamlessly handle local and server data
  - **Transparent Operations**: Components work identically with both data sources
  - **Automatic Fallbacks**: API failures gracefully switch to local storage
  - **Optimized State Management**: Minimal overhead for dual-source operations
  - **Error Boundary Protection**: Robust error handling prevents data loss

- **Enterprise-Grade Sync System**: Reliable, scalable data transfer
  - **Atomic Operations**: All-or-nothing sync approach prevents partial failures
  - **Conflict Resolution**: Intelligent handling of data conflicts during sync
  - **Comprehensive Logging**: Detailed tracking for debugging and monitoring
  - **Scalable Design**: Architecture supports future enhancements and growth

## Impact
This revolutionary update transforms VSLogger from a registered-user platform into a universally accessible tool. New users can immediately experience the full power of VSLogger, with their work automatically preserved when they decide to create an account. This dramatically reduces conversion friction while maintaining data integrity and user trust.`,

  '2.1.1': `# PATCH NOTES - VSLogger v2.1.1

## Navigation System Overhaul & Move Analytics

### Revolutionary New Features
- **Unified Dropdown Navigation**: Complete redesign of navigation system with dropdown menus
  - **Analytics Dropdown**: Unified Analytics button with dropdown options for TCG Analytics and VGC Analytics
  - **My Lists Dropdown**: Unified My Lists button with dropdown options for TCG Decks and VGC Teams
  - Available on both desktop navbar and mobile sidebar for consistent experience
  - Smart context-aware navigation - main buttons navigate to appropriate section based on current page
  - Dropdown arrows provide explicit choice between TCG and VGC variants

- **Pokémon Move Usage Analytics**: Revolutionary new feature showing individual Pokémon move usage patterns
  - **Move Usage Pie Charts**: Small, elegant pie charts in each Pokémon usage card displaying top moves
  - **Smart Data Presentation**: Shows top 4 most-used moves plus "Others" category for clarity
  - **Interactive Tooltips**: Hover for detailed move usage statistics and percentages
  - **Comprehensive Backend**: New API endpoint tracking move usage per Pokémon from replay data
  - **Responsive Design**: Charts scale perfectly on mobile and desktop devices
  - **Theme-Aware**: Charts adapt colors to match light/dark theme settings

- **Smart Analytics Navigation**: Analytics link now intelligently navigates based on current context
  - When viewing VGC pages (teams, VGC matches, VGC logs), Analytics navigates to VGC Analytics
  - When viewing TCG pages (decks, TCG matches, TCG logs), Analytics navigates to TCG Analytics
  - Dashboard tab state is respected (VGC tab active → VGC Analytics)
  - Seamless context-aware navigation improves user workflow

### Enhanced User Experience
- **Enhanced Navigation UX**: Improved navigation interaction patterns
  - Click-based dropdown functionality (not just hover)
  - Visual feedback with rotating chevron arrows
  - Active state highlighting for current pages
  - Mutual dropdown exclusivity (opening one closes others)
  - Click outside to close functionality

- **Improved Page Spacing**: Added more comfortable spacing between navbar and page content
  - Increased top padding from 80px to 100px for better visual breathing room
  - Enhanced overall page layout and readability

- **Cross-Platform Consistency**: Navigation features work identically on desktop and mobile
  - Desktop navbar dropdown functionality matches mobile sidebar
  - Consistent styling and behavior across all devices
  - Responsive design maintains usability on all screen sizes

### Technical Achievements
- **Advanced Analytics Backend**: New VGC match controller endpoint for Pokémon-specific move usage statistics
- **Chart.js Integration**: Enhanced chart components with theme-responsive pie charts
- **Data Processing Pipeline**: Sophisticated move data extraction and normalization from replay logs
- **Component Architecture**: New PokemonMovePieChart component with responsive design patterns
- **Dropdown Navigation**: Comprehensive dropdown system in both Navbar.js and Sidebar.js
- **Enhanced State Management**: Improved state controls for dropdown visibility and move usage data
- **Context-Aware Navigation**: Smart routing using useLocation and localStorage
- **CSS Architecture**: Enhanced styling with move chart specific classes and responsive breakpoints

## Impact
This update transforms VSLogger into a more intuitive and analytically powerful platform, providing unprecedented insights into Pokémon move usage patterns while dramatically improving navigation efficiency across both TCG and VGC gameplay tracking.`,

  '2.0': `# Pokématch VS Logger - Patch Notes

## Major Updates (v2.0)

### Features
- **VGC Integration**: Complete integration of Pokémon VGC (Video Game Championships) match logging
- **Friends System**: New social features allowing players to connect with each other
- **Notifications**: Real-time updates for friend requests and activities
- **Unified Match System**: Completely redesigned match logging system supporting both TCG and VGC
- **Enhanced Analytics**: Separate analytics views for TCG and VGC with improved metrics
- **Profile Settings**: Expanded user profile management
- **Improved UI**: Redesigned layout with sidebar navigation and notification bell
- **Showdown Team Builder**: Enhanced support for importing Pokémon Showdown team data

### Technical Changes
- **Architecture**: Migrated from single match model to specialized TCGMatch and VGCMatch models
- **API**: Extended API endpoints for VGC matches, friends, and notifications
- **Migration**: Added migration scripts for transitioning data to new schema
- **Deployment**: Updated configuration for Vercel (frontend) and Render (backend) deployment
- **Documentation**: Enhanced README with detailed deployment instructions

## Recent Fixes
- Added Pokémon sprite selection for TCG decks with dynamic searching
- Added retroactive sprite assignment for existing TCG matches
- Fixed 500 error when adding or updating decks
- Fixed logout button not appearing in mobile sidebar menu
- Fixed VGC matches not displaying in Match History
- Fixed profile picture removal functionality in user settings
- Fixed syntax error in LogMatchVGC.js
- Fixed issues with replay handling
- Temporarily removed Best-of-3 replay auto-import feature due to stability issues
- Fixed server-side validation for energy card limits in deck builder
- Removed 4-card limit for energy cards in deck builder
- Fixed authentication issues for production deployment
- Enhanced CORS handling for authentication routes
- Improved error handling throughout the application

## Deployment Updates
- Added configurations for automated deployment on Vercel and Render
- Updated environment variable handling for production environments
- Fixed API URL configuration for production deployment

## Known Issues
- Best-of-3 (BO3) replay auto-import feature has been temporarily disabled

## Coming Soon
- Restore and improve BO3 replay auto-import feature
- Enhanced mobile responsiveness
- Team sharing features
- Tournament tracking and management`,

  '2.0.1': `# Pokématch VS Logger - Patch Notes

## Updates (v2.0.1)

### Fixes
- **Deck Sprite Saving**: Fixed issue where Pokémon sprites weren't being saved properly when creating or updating TCG
decks
- **Field Consistency**: Improved how deck sprites are handled between client and server by supporting both legacy and new
field names
- **Error Handling**: Enhanced error logging for deck sprite operations for easier troubleshooting

### Technical Improvements
- **API Robustness**: Updated deck API endpoints to properly handle both \`sprite\` and \`pokemonSprite\` fields
- **Data Integrity**: Ensured consistent storage of sprite data across the application
- **Backward Compatibility**: Maintained support for older client versions accessing sprite data

## Previous Fixes
- Added Pokémon sprite selection for TCG decks with dynamic searching
- Added retroactive sprite assignment for existing TCG matches
- Fixed 500 error when adding or updating decks
- Fixed logout button not appearing in mobile sidebar menu
- Fixed VGC matches not displaying in Match History
- Fixed profile picture removal functionality in user settings
- Fixed syntax error in LogMatchVGC.js
- Fixed issues with replay handling
- Temporarily removed Best-of-3 replay auto-import feature due to stability issues
- Fixed server-side validation for energy card limits in deck builder
- Removed 4-card limit for energy cards in deck builder
- Fixed authentication issues for production deployment
- Enhanced CORS handling for authentication routes
- Improved error handling throughout the application

## Known Issues
- Best-of-3 (BO3) replay auto-import feature has been temporarily disabled

## Coming Soon
- Restore and improve BO3 replay auto-import feature
- Enhanced mobile responsiveness
- Team sharing features
- Tournament tracking and management`,

  '2.1.0': `# PATCH NOTES - VSLogger v2.1.0

## Shared Match Groups & Collaboration

### Revolutionary Social Features
- **Shared Match Groups**: Game-changing collaboration system for competitive players
  - Invite friends to your tournament groups and track results together
  - Group owners can set customizable permissions (View or Edit access)
  - Friends with edit permission can contribute matches to shared collections
  - Perfect for team tournaments, practice groups, and collaborative tracking
- **Advanced Invitation System**: Complete workflow for group sharing
  - Send invitations directly from group management interface
  - Recipients can accept or decline invitations through dedicated modal
  - Real-time status tracking (pending, accepted, declined)
  - Visual indicators show shared groups and your role in each group

### Analytics Redesign & Enhancements
- **Complete Analytics Overhaul**: Enhanced data visualization and filtering
  - New match group filtering with intelligent UI interactions
  - Gradient progress bars that dynamically color-code performance (red → yellow → green)
  - Redesigned deck matchups as sortable list view (highest to lowest win rate)
  - Clickable matchup cards open detailed analysis modals with match history
  - Moved turn order statistics to individual matchup cards for focused analysis
- **Advanced Modal System**: Comprehensive matchup analysis
  - View all matches for specific opponent deck matchups
  - Compact match display showing date, format, game results sequence, and notes
  - Pagination support for large match histories (10 matches per page)
  - Enhanced mobile-responsive design for all screen sizes
- **Smart Filter Management**: 
  - Match group filter overrides deck filter to prevent conflicts
  - Automatic switching between filter types with visual feedback
  - Disabled state indicators show when filters are unavailable

### Layout & Design Improvements
- **Optimized Page Layouts**: Enhanced readability and focus
  - Reduced analytics and dashboard content width by 15% for better scanning
  - Improved visual hierarchy and content organization
  - Consistent responsive behavior across all device sizes
- **Enhanced User Experience**:
  - Fixed modal z-index issues preventing proper display
  - Added content padding to prevent navbar/header overlap
  - Improved button layouts and visual feedback
  - Updated navigation with patch notes access

### Technical Architecture
- **Backend Infrastructure**: Complete shared groups system
  - Enhanced MatchGroup model with sharing and invitation capabilities
  - Secure permission-based access control (view/edit roles)
  - New API endpoints for friend invitations and group management
  - Robust invitation workflow with status tracking and validation
- **Frontend Components**: Modular sharing system
  - ShareGroupModal: Intuitive interface for inviting friends
  - InvitationsModal: Comprehensive invitation management
  - Enhanced MatchGroupItem: Visual sharing indicators and action buttons
  - Updated context providers with new collaboration functions
- **Mobile Optimization**: Touch-friendly collaboration features
  - Responsive modal designs for small screens
  - Optimized invitation management for mobile users
  - Enhanced accessibility across all new features

## Impact
This update transforms VSLogger from an individual tracking tool into a collaborative platform for competitive Pokémon players. Teams can now share tournament results, practice groups can track collective progress, and friends can easily collaborate on match data collection.`,

  '2.0.9': `# PATCH NOTES - VSLogger v2.0.9

## Admin, Filtering & Access Control Updates

### New Features
- **Advanced Role System**: Added a new role hierarchy for better access control
  - Added "Owner" role above admin with exclusive permissions
  - Implemented secure role verification middleware
  - Enhanced user management capabilities
- **Admin Management**: New dashboard for owner role to manage admins
  - Search for users by username
  - Promote regular users to admin role
  - Demote admins to regular users
  - View all current admins and their status
- **Analytics Filtering**: Enhanced analytics with deck filtering
  - Filter match data by specific deck played
  - More focused analysis of deck performance
  - Improved data visualization by played deck

### Technical Improvements
- **Role-Based Security**: Implemented robust role-based access control system
  - Added secure middleware for owner-only routes
  - Updated user model with role field
  - Enhanced authentication endpoints with role information
- **Database Connection Handling**: Improved error handling for database operations
  - Added better error messaging for connection issues
  - Enhanced setup scripts with proper error handling
  - Improved debugging information for connection problems`,

  '2.0.8': `# PATCH NOTES - VSLogger v2.0.8

## Deck Management & Analytics Enhancements

### New Features
- **Deck Detail Modal**: Added a new comprehensive deck viewer modal
  - Shows complete card lists with counts by category
  - Displays deck statistics and composition breakdown
  - Enhanced visual styling with card type highlighting
- **Deck Management Improvements**: 
  - Added deck copying functionality 
  - Improved deck import with better error handling and naming
  - Enhanced deck builder with reset on close for cleaner sessions
- **Analytics Visualization**: 
  - Added clickable deck names in analytics displays
  - Replaced small sprite images with readable deck names
  - Improved visual indication of deck usage in match history

### UI Improvements
- **Deck Item Design**: Updated deck card styling with modern design
  - New button layout with improved visual hierarchy
  - Better visibility for active deck indication
  - Consistent styling across light and dark modes
- **Team Item Styling**: Refined active badge presentation for consistency
- **General Visual Enhancements**:
  - Improved spacing and alignment throughout the application
  - Enhanced buttons with new hover and active states
  - Better contrast for text elements in both themes

### Technical Improvements
- **Export Utilities**: 
  - Improved deck import with name detection
  - Enhanced card type detection logic
  - Better error handling with detailed failure reporting
- **Component Architecture**:
  - New modular approach for deck detail viewing
  - Improved state management for deck operations
  - Optimized rendering for large deck collections`,

  '2.0.7': `# PATCH NOTES - VSLogger v2.0.7

## PTCGL Match Log Integration & UI Improvements

### New Features
- **PTCGL Match Log Parser**: Added the ability to automatically import PTCGL match logs
  - Extracts opponent name, match result, mulligans, and prize card information
  - Supports case-insensitive username matching
  - Automatically identifies win/loss status based on log content
- **Bo3/Bo5 Support for PTCGL Imports**: 
  - Added support for multi-game series when importing PTCGL matches
  - Series automatically tracks wins/losses to determine completion
  - Consistently preserves match group and opponent deck selection across games
- **UI Enhancements**:
  - Added toggle option to hide optional questions for faster TCG match logging
  - Updated home page to include latest patch notes section

### Technical Improvements
- **Performance Optimization**:
  - Fixed issue with excessive API calls in the PTCGL import component
  - Improved state management for multi-game series
  - Enhanced handling of large match logs with better error reporting
- **Bug Fixes**:
  - Fixed match series tracking to correctly detect when a Bo3/Bo5 series is complete
  - Resolved issue with conditional UI updates in match form
  - Added defensive coding to prevent memory leaks from multiple API calls`,

  '2.0.6': `# PATCH NOTES - VSLogger v2.0.6

## Bug Fixes
- **Fixed Feedback Deletion**: Resolved 500 error when deleting feedback submissions in the admin portal
  - Added proper validation for MongoDB ObjectId format
  - Improved error handling with detailed logging
  - Replaced findByIdAndRemove with deleteOne for better compatibility

## UI Improvements
- **Enhanced Patch Notes Display**: Fixed rendering issues in the announcements page
  - Updated to display patch notes directly in the component instead of fetching from markdown files
  - Eliminated code block rendering issues with proper formatting
  - Improved version selection with dedicated sections for each release

## Technical Improvements
- **Admin Portal Stability**: Enhanced error handling in feedback management
- **Documentation**: Updated deployment and configuration instructions`,

  '2.0.5': `# PATCH NOTES - VSLogger v2.0.5

## TCG Match Logging Enhancements

## New Fields and Process Flow

- Restructured TCG match logging process to a two-page flow:
  - Page 1: Match Details (deck selection, match format, group)
  - Page 2: Game Details (opponent deck, prize cards, results)
- Added new data fields to TCG matches:
  - Player Mulligans: Track how many mulligans you took
  - Opponent Mulligans: Track how many mulligans your opponent took
  - Concession Tracking: Record if either player conceded the game

## Match Editing

- Added ability to edit previously logged TCG matches
- Added edit button to match details modal
- Created route for match editing: /edit-match/tcg/:id
- All match data is preserved when editing, including:
  - Deck selections
  - Group assignments
  - Game details and results
  - Prize card information

## UI/UX Improvements

- Clearly marked optional fields with "(Optional)" label
- Improved organization of match logging form
- Prize card content dropdowns now appear directly beneath corresponding prize card
- Enhanced visual hierarchy of match logging form

## Mobile Experience

- Added extra padding to popup menus for better mobile scrolling
- Improved layout for small screens
- Fixed accessibility issues with bottom menu items on touch screens

## Technical Updates

- Ensured backward compatibility with existing matches
- Updated TCGMatch model with new fields
- Enhanced match details modal to show new data when available
- Improved state management for multi-game matches

## Bug Fixes

- Fixed issue with match history not displaying complete match details
- Improved error handling during match submission
- Improved component loading for better performance

---
These updates expand the depth of match data that can be tracked in TCG matches while making the overall process more
intuitive and organized. The new editing capability allows players to correct mistakes or add missing information to
previously logged matches.`
};

// Define available versions
export const availableVersions = [
  { version: '2.6.0', title: 'PokeMatch v2.6.0 - Public API, New Sets & Fixes' },
  { version: '2.5.0', title: 'PokeMatch v2.5.0 - Full Visual Redesign, Streamlined Logging & Smarter Archetypes' },
  { version: '2.4.1', title: 'PokeMatch v2.4.1 - Bug Fixes & Quality of Life' },
  { version: '2.4.0', title: 'PokeMatch v2.4.0 - Match Import Overhaul, CSV Export & UI Fixes' },
  { version: '2.3.2', title: 'PokeMatch v2.3.2 - Tournament Analytics & Meta Analysis System' },
  { version: '2.3.1', title: 'PokeMatch v2.3.1 - Limitless TCG Integration & Decklist Import' },
  { version: '2.3.0', title: 'PokeMatch v2.3.0 - Major Infrastructure Overhaul & Multi-Language Support' },
  { version: '2.2.1', title: 'VSLogger v2.2.1 - Major Dashboard Overhaul & Analytics Fixes' },
  { version: '2.2.0', title: 'VSLogger v2.2.0 - VGC Match Enhancements & Non-Authenticated User Support' },
  { version: '2.1.1', title: 'VSLogger v2.1.1 - Navigation & Move Analytics' },
  { version: '2.1.0', title: 'VSLogger v2.1.0 - Shared Groups & Collaboration' },
  { version: '2.0.9', title: 'VSLogger v2.0.9' },
  { version: '2.0.8', title: 'VSLogger v2.0.8' },
  { version: '2.0.7', title: 'VSLogger v2.0.7' },
  { version: '2.0.6', title: 'VSLogger v2.0.6' },
  { version: '2.0.5', title: 'VSLogger v2.0.5' },
  { version: '2.0.1', title: 'Updates (v2.0.1)' },
  { version: '2.0', title: 'Major Updates (v2.0)' }
];

// Function to get the latest patch notes (first 3 lines of latest version)
export const getLatestPatchNotesPreview = () => {
  const latestVersion = availableVersions[0];
  // Use 'all' content since that's where all patch notes are stored
  const content = patchNotesContent['all'];

  // Find the section for the latest version
  const lines = content.split('\n');
  let versionStartIndex = -1;
  let versionEndIndex = lines.length;

  // Find where the latest version starts
  const latestTag = `## Updates (v${latestVersion.version})`;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(latestTag)) {
      versionStartIndex = i;
    } else if (versionStartIndex > -1 && i > versionStartIndex && lines[i].startsWith('## Updates (v')) {
      versionEndIndex = i;
      break;
    }
  }

  // Extract the latest version section
  const versionLines = lines.slice(versionStartIndex, Math.min(versionStartIndex + 15, versionEndIndex));
  const preview = versionLines.join('\n');

  return {
    version: latestVersion.version,
    title: latestVersion.title,
    preview: preview
  };
};